import {
  Heart,
  MapPin,
  Plus,
  Share2,
  ExternalLink,
  BellRing,
  Copy,
  Store,
  ChevronDown,
  Flame,
  Bell,
  Trash2,
  Package,
} from "lucide-react";
import type { MockProduct } from "../utils/landingFeatures";
import { getProductImageUrl } from "../utils/imageUtils";

const fmt = (n: number) => `$${n.toFixed(2)}`;
const pct = (msrp: number, price: number) =>
  msrp > 0 && price > 0 ? Math.round(((msrp - price) / msrp) * 100) : 0;

const BADGE_COLORS: Record<string, string> = {
  "NEW": "bg-emerald-500",
  "PRICE↓": "bg-green-500",
  "LOWEST": "bg-violet-500",
  "STOCK↑": "bg-blue-500",
  "HOT": "bg-orange-500",
  "RARE": "bg-fuchsia-500",
};

interface BaseProps {
  product: MockProduct;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Product card — mirrors web/src/components/ProductItem.tsx
 * Used by: in-store-deals, product-search
 * ────────────────────────────────────────────────────────────────────────── */
export function LandingProductCard({ product }: BaseProps) {
  const discount = pct(product.msrp, product.price);
  const savings = Math.max(0, product.msrp - product.price);

  return (
    <div className="bg-white rounded-2xl border border-violet-100 shadow-[0_18px_50px_-22px_rgba(76,29,149,0.22)] overflow-hidden">
      <div className="px-3.5 py-3">
        <div className="flex items-stretch gap-3">
          {/* Image + badge + stock */}
          <div className="flex flex-col items-center">
            <div className="w-[84px] h-[84px] rounded-xl bg-gray-50 border border-violet-100/40 overflow-hidden flex-shrink-0 flex items-center justify-center">
              <img
                src={getProductImageUrl(product.image, "400")}
                alt=""
                className="w-full h-full object-contain"
              />
            </div>
            {product.badge && (
              <div
                className={`relative -mt-2 px-1.5 py-0.5 rounded text-[9px] font-bold text-white uppercase tracking-wide shadow-md ${
                  BADGE_COLORS[product.badge] || "bg-orange-500"
                }`}
              >
                {product.badge}
              </div>
            )}
            <div className="mt-1 w-[88px] flex items-center justify-center gap-1 text-[10px]">
              <div
                className={`w-[6px] h-[6px] rounded-full ${
                  product.stock > 0 ? "bg-emerald-500" : "bg-red-500"
                }`}
              />
              <span
                className={`font-medium truncate ${
                  product.stock > 0 ? "text-emerald-700" : "text-gray-500"
                }`}
              >
                {product.stock > 0 ? `${product.stock} in stock` : "Out"}
              </span>
            </div>
          </div>

          {/* Right column */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex justify-between items-start gap-2">
              <span className="flex-1 min-w-0 font-semibold text-[14px] leading-[18px] text-[#1e1b3a] line-clamp-2">
                {product.name}
              </span>
              <button
                type="button"
                aria-label="Save"
                className="p-1.5 text-gray-300 hover:text-rose-500 transition-colors rounded-md hover:bg-violet-50 flex-shrink-0"
              >
                <Heart className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-1.5 mt-1 min-w-0">
              <span className="text-[11px] font-medium text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded-full truncate">
                {product.storeLabel}
              </span>
            </div>

            <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
              <span className="text-[18px] font-extrabold text-emerald-600">
                {fmt(product.price)}
              </span>
              {product.msrp > product.price && (
                <span className="text-[12px] text-gray-400 line-through">
                  {fmt(product.msrp)}
                </span>
              )}
              {savings > 0 && (
                <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
                  Save {fmt(savings)}
                </span>
              )}
              {discount > 0 && (
                <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                  {discount}% off
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[11px] text-gray-700">UPC: {product.upc}</span>
              {product.aisle && (
                <span className="text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-50 px-1.5 py-0.5 rounded">
                  Aisle {product.aisle}
                </span>
              )}
              {product.pickupAvailable && (
                <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                  Pickup
                </span>
              )}
            </div>

            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[10px] text-gray-500 truncate">
                {product.changedAgo}
                {product.updatedAgo && product.changedAgo !== product.updatedAgo
                  ? ` · seen ${product.updatedAgo}`
                  : ""}
              </span>
              <span className="w-7 h-7 flex items-center justify-center rounded-full border bg-white border-gray-200 text-gray-600">
                <Plus className="w-4 h-4" />
              </span>
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-violet-50 flex items-center gap-1.5">
          <span className="flex-1 flex items-center justify-center py-1.5 px-2.5 rounded-md text-[11px] font-semibold bg-orange-100 text-orange-700">
            <MapPin className="w-3 h-3 mr-1" />
            Nearby
          </span>
          <span className="flex-1 flex items-center justify-center py-1.5 px-2.5 rounded-md text-[11px] font-semibold bg-amber-100 text-amber-700">
            <BellRing className="w-3 h-3 mr-1" />
            Set alert
          </span>
          <span className="flex-1 flex items-center justify-center py-1.5 px-2.5 rounded-md text-[11px] font-medium bg-blue-100 text-blue-700">
            <ExternalLink className="w-3 h-3 mr-1" />
            View deal
          </span>
          <span className="flex items-center justify-center py-1.5 px-2 rounded-md text-gray-400">
            <Share2 className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Lead card — mirrors web/src/components/TopLeadsView.tsx DealCard
 * Used by: hot-leads
 * ────────────────────────────────────────────────────────────────────────── */
export function LandingLeadCard({ product, hottest = false }: BaseProps & { hottest?: boolean }) {
  const discount = pct(product.msrp, product.price);
  const borderClass = hottest
    ? "border-violet-200 ring-1 ring-violet-200/40"
    : "border-gray-200";

  return (
    <div
      className={`group w-[168px] max-w-full mx-auto rounded-2xl border bg-white overflow-hidden flex flex-col flex-shrink-0 ${borderClass} shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="relative w-full h-[168px] bg-gray-50 flex-shrink-0 overflow-hidden">
        <img
          src={getProductImageUrl(product.image, "400")}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain p-2"
        />
        {product.changedAgo && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/95 text-gray-800 text-[10px] font-bold shadow-sm">
            <span>{product.changedAgo}</span>
          </div>
        )}
        <div className="absolute top-1.5 right-1.5 rounded-full bg-white/95 p-1 shadow-sm">
          <Heart className="w-3 h-3 text-gray-400" />
        </div>
        {hottest && (
          <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-rose-500 text-white text-[8px] font-extrabold shadow-md uppercase tracking-wide">
            <Flame className="w-2 h-2 fill-white" />
            Hot
          </div>
        )}
      </div>

      <div className="px-2.5 pt-2 pb-2 flex-1 flex flex-col gap-0.5">
        <div
          className="text-[12px] font-semibold leading-[15px] text-[#1e1b3a] overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            minHeight: 30,
          }}
        >
          {product.name}
        </div>
        <div className="flex items-baseline flex-wrap gap-1 mt-auto pt-1">
          <span className="text-[16px] font-extrabold text-emerald-600 tabular-nums">
            {fmt(product.price)}
          </span>
          {product.msrp > product.price && (
            <span className="text-[11px] text-gray-400 line-through tabular-nums">
              {fmt(product.msrp)}
            </span>
          )}
        </div>
        {discount > 0 && (
          <div className="flex items-center gap-1">
            <span
              className={`text-[11px] font-extrabold ${
                discount > 70 ? "text-orange-600" : "text-violet-700"
              }`}
            >
              {discount}% off
            </span>
            {discount > 70 && (
              <Flame className="w-2.5 h-2.5 text-orange-500 fill-orange-500" />
            )}
          </div>
        )}
      </div>

      <div className="flex items-stretch gap-1 px-2 py-1.5 border-t border-gray-100">
        <span className="h-7 flex-1 min-w-0 inline-flex items-center justify-center gap-0.5 rounded-md text-[10px] font-semibold bg-violet-50 text-violet-700">
          <BellRing className="w-2.5 h-2.5 shrink-0" />
          Alert
        </span>
        <span className="h-7 flex-1 min-w-0 inline-flex items-center justify-center gap-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700">
          <MapPin className="w-2.5 h-2.5 shrink-0" />
          Nearby
        </span>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Tracker card — mirrors web/src/pages/TracksPageNew.tsx TrackerCard
 * Used by: price-alerts
 * ────────────────────────────────────────────────────────────────────────── */
export function LandingTrackerCard({ product }: BaseProps) {
  const target = product.targetPrice ?? product.price;
  const isReached = product.price <= target;
  const diff = product.price - target;

  return (
    <div className="bg-white rounded-2xl border border-violet-100 shadow-[0_18px_50px_-22px_rgba(76,29,149,0.22)] overflow-hidden">
      <div className="p-3.5">
        <div className="flex gap-3">
          <div className="w-[60px] h-[60px] rounded-xl flex-shrink-0 overflow-hidden bg-gray-50 flex items-center justify-center border border-violet-100/40">
            {product.image ? (
              <img
                src={getProductImageUrl(product.image, "400")}
                alt=""
                className="w-full h-full object-contain p-1"
              />
            ) : (
              <Package className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[13.5px] font-semibold leading-snug text-[#1e1b3a] line-clamp-2">
              {product.name}
            </h3>
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 mt-1.5 text-[11px] font-medium text-gray-500">
              <span className="inline-flex items-center gap-1">
                <span className="font-mono">{product.upc}</span>
                <Copy className="w-2.5 h-2.5 text-gray-400" />
              </span>
              <span className="text-gray-300">·</span>
              {product.zipCode && (
                <>
                  <span className="inline-flex items-center gap-0.5">
                    <MapPin className="w-2.5 h-2.5 text-gray-400" />
                    {product.zipCode}
                  </span>
                  <span className="text-gray-300">·</span>
                </>
              )}
              <span className="text-gray-400">{product.updatedAgo}</span>
            </div>
          </div>
        </div>

        <div className="flex items-baseline gap-2.5 flex-wrap mt-3">
          <span
            className={`text-2xl font-extrabold tabular-nums [letter-spacing:-0.6px] ${
              isReached ? "text-emerald-600" : "text-[#1e1b3a]"
            }`}
          >
            {fmt(product.price)}
          </span>
          <div className="flex items-center flex-wrap gap-1.5 min-w-0">
            {isReached && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10.5px] font-bold tracking-wide">
                <Store className="w-2.5 h-2.5" />
                In store
              </span>
            )}
            <span className="text-[12.5px] font-medium tabular-nums text-gray-400">
              Target {fmt(target)}
            </span>
            {isReached ? (
              <span className="text-[12.5px] font-bold tabular-nums text-emerald-600">
                · Ready
              </span>
            ) : diff > 0 ? (
              <span className="text-[12.5px] font-bold tabular-nums text-amber-600">
                · ${diff.toFixed(2)} over
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between px-3.5 py-2.5 border-t border-gray-100">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500">
          <Store className="w-3.5 h-3.5" />
          {isReached ? "1 store in stock" : "Watching · no match yet"}
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        </span>
        <div className="flex items-center gap-1">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400">
            <Bell className="w-3.5 h-3.5" />
          </span>
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400">
            <Trash2 className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * Smart switch — pick the right card based on style
 * ────────────────────────────────────────────────────────────────────────── */
export default function LandingFeatureCard({
  product,
  style,
  hottest,
}: BaseProps & { style: "product" | "lead" | "tracker"; hottest?: boolean }) {
  if (style === "lead") return <LandingLeadCard product={product} hottest={hottest} />;
  if (style === "tracker") return <LandingTrackerCard product={product} />;
  return <LandingProductCard product={product} />;
}
